type TemplatePickerResolve = (slug: string) => void;

const PREVIEW_SELECTOR = "iframe[data-template-preview-frame][data-preview-src]";

function hydratePreview(frame: HTMLIFrameElement): void {
    const src = frame.dataset.previewSrc ?? "";
    if (src === "" || frame.dataset.previewLoaded === "1") return;
    frame.src = src;
    frame.dataset.previewLoaded = "1";
}

export function initCmsTemplateLazyPreviews(scope: Document | HTMLElement = document): void {
    const frames = Array.from(scope.querySelectorAll<HTMLIFrameElement>(PREVIEW_SELECTOR))
        .filter((frame) => frame.dataset.templatePreviewBound !== "1");
    if (frames.length === 0) return;

    if (!("IntersectionObserver" in window)) {
        frames.forEach((frame) => {
            frame.dataset.templatePreviewBound = "1";
            hydratePreview(frame);
        });
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const frame = entry.target as HTMLIFrameElement;
            hydratePreview(frame);
            observer.unobserve(frame);
        });
    }, { rootMargin: "320px 0px" });

    frames.forEach((frame) => {
        frame.dataset.templatePreviewBound = "1";
        observer.observe(frame);
    });
}

export function initCmsTemplatePicker(picker: HTMLElement, resolve: TemplatePickerResolve): void {
    initCmsTemplateLazyPreviews(picker);

    const cards = Array.from(picker.querySelectorAll<HTMLElement>("[data-template-component-choice]"));
    const search = picker.querySelector<HTMLInputElement>("[data-template-component-search]");
    const empty = picker.querySelector<HTMLElement>("[data-template-component-empty]");
    const detail = picker.querySelector<HTMLElement>("[data-template-component-detail]");
    const detailFrame = picker.querySelector<HTMLIFrameElement>("[data-template-component-detail-frame]");
    const detailTitle = picker.querySelector<HTMLElement>("[data-template-component-detail-title]");
    const detailSlug = picker.querySelector<HTMLElement>("[data-template-component-detail-slug]");
    const detailCategory = picker.querySelector<HTMLElement>("[data-template-component-detail-category]");
    const detailOpen = picker.querySelector<HTMLAnchorElement>("[data-template-component-detail-open]");
    const detailUse = picker.querySelector<HTMLButtonElement>("[data-template-component-detail-use]");

    const selectCard = (card: HTMLElement): void => {
        cards.forEach((item) => item.classList.toggle("is-selected", item === card));
        const slug = card.dataset.templateSlug ?? "";
        const name = card.dataset.templateName ?? slug;
        const category = card.dataset.templateCategory ?? "";
        const previewUrl = card.dataset.templatePreviewUrl ?? "";

        if (detail) detail.hidden = false;
        if (detailTitle) detailTitle.textContent = name;
        if (detailSlug) detailSlug.textContent = slug;
        if (detailCategory) detailCategory.textContent = category;
        if (detailOpen) {
            detailOpen.href = previewUrl || "#";
            detailOpen.hidden = previewUrl === "";
        }
        if (detailUse) detailUse.dataset.templateSlug = slug;
        if (detailFrame && previewUrl !== "" && detailFrame.dataset.currentPreviewUrl !== previewUrl) {
            detailFrame.src = previewUrl;
            detailFrame.dataset.currentPreviewUrl = previewUrl;
        }
    };

    const firstVisible = (): HTMLElement | null => cards.find((card) => !card.hidden) ?? null;

    search?.addEventListener("input", () => {
        const q = search.value.trim().toLowerCase();
        let visible = 0;
        cards.forEach((card) => {
            const matched = q === "" || (card.dataset.search ?? "").includes(q);
            card.hidden = !matched;
            if (matched) visible += 1;
        });
        picker.querySelectorAll<HTMLElement>("[data-template-component-group]").forEach((group) => {
            const hasVisible = !!group.querySelector<HTMLElement>("[data-template-component-choice]:not([hidden])");
            group.hidden = !hasVisible;
        });
        if (empty) empty.hidden = visible > 0;
        const selected = cards.find((card) => card.classList.contains("is-selected") && !card.hidden);
        const next = selected ?? firstVisible();
        if (next) selectCard(next);
        else if (detail) detail.hidden = true;
    });

    picker.addEventListener("click", (event) => {
        const target = event.target as HTMLElement | null;
        const useButton = target?.closest<HTMLElement>("[data-template-component-use]");
        if (useButton && picker.contains(useButton)) {
            event.preventDefault();
            const slug = useButton.dataset.templateSlug ?? "";
            if (slug !== "") resolve(slug);
            return;
        }

        const previewButton = target?.closest<HTMLElement>("[data-template-component-preview]");
        if (!previewButton || !picker.contains(previewButton)) return;
        event.preventDefault();
        const card = previewButton.closest<HTMLElement>("[data-template-component-choice]");
        if (card) selectCard(card);
    });

    const initial = firstVisible();
    if (initial) selectCard(initial);
}
